import { StopsellApplyPayload } from '~/types/stopsell';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { DeletedBlackoutRange, collapseWithinSource, reconcileBlackoutSchedule } from './blackoutReconciliation';
import type { SalesforceBlackoutDate } from './types';

export interface RoomRateWizardPlan {
  roomTypeId: string;
  roomTypeName: string;
  roomRateId: string;
  ratePlanName: string;
  blocksToDelete: StopsellApplyPayload['rooms'][number]['operations'][number]['blocksToDelete'];
  deletedRanges: Array<DeletedBlackoutRange>;
  rangesToCreate: StopsellApplyPayload['rooms'][number]['operations'][number]['rangesToCreate'];
}

export function buildWizardApplyPlan(
  selectedBlackoutDates: Array<SalesforceBlackoutDate>,
  selectedRoomRates: Array<RoomRateSelection>,
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>,
): Array<RoomRateWizardPlan> {
  if (selectedBlackoutDates.length === 0) {
    return [];
  }

  const desiredRanges = collapseWithinSource(
    selectedBlackoutDates.map((blackoutDate) => ({
      id: blackoutDate.sfid,
      start: blackoutDate.from__c,
      end: blackoutDate.to__c,
    })),
    'salesforce',
  );

  return selectedRoomRates.map(({ roomType, roomRate }) => {
    const result = reconcileBlackoutSchedule(roomRate.id, desiredRanges, blackoutDatesByRoomRate[roomRate.id] ?? []);

    return {
      roomTypeId: roomType.id,
      roomTypeName: roomType.name,
      roomRateId: roomRate.id,
      ratePlanName: roomRate.rate_plan.name,
      blocksToDelete: result.blocksToDelete,
      deletedRanges: result.deletedRanges,
      rangesToCreate: result.rangesToCreate,
    };
  });
}

export function getDeletedRangeKey(range: DeletedBlackoutRange): string {
  return `${range.roomRateId}:${range.id}`;
}

export function applyBlockExclusions(
  plans: Array<RoomRateWizardPlan>,
  excludedBlockKeys: ReadonlySet<string>,
): Array<RoomRateWizardPlan> {
  if (excludedBlockKeys.size === 0) {
    return plans;
  }

  return plans.map((plan) => {
    const deletedRanges = plan.deletedRanges.filter((range) => !excludedBlockKeys.has(getDeletedRangeKey(range)));
    const keptIds = new Set(deletedRanges.map((range) => range.id));

    return {
      ...plan,
      blocksToDelete: plan.blocksToDelete.filter((block) => keptIds.has(block.id)),
      deletedRanges,
    };
  });
}

export function buildWizardApplyPayload(plans: Array<RoomRateWizardPlan>): StopsellApplyPayload {
  const roomsById = new Map<string, StopsellApplyPayload['rooms'][number]>();

  plans
    .filter((plan) => plan.blocksToDelete.length > 0 || plan.rangesToCreate.length > 0)
    .forEach((plan) => {
      const room = roomsById.get(plan.roomTypeId) ?? {
        roomTypeId: plan.roomTypeId,
        operations: [{ operation: 'blackout' as const, blocksToDelete: [], rangesToCreate: [] }],
      };
      room.operations[0].blocksToDelete.push(...plan.blocksToDelete);
      room.operations[0].rangesToCreate.push(...plan.rangesToCreate);
      roomsById.set(plan.roomTypeId, room);
    });

  return { rooms: Array.from(roomsById.values()) };
}
